import React, { useEffect, useState } from 'react';
import { brokerApi, aiApi } from './api';
import StockLink from './StockLink';

const RISK_LEVELS = ['conservative', 'moderate', 'aggressive'];

export default function AIManager({ onTradeAction }) {
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState(null);
  const [riskTolerance, setRiskTolerance] = useState('moderate');
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    brokerApi.account().then(setAccount).catch(() => {});
    brokerApi.positions().then(setPositions).catch(() => {});
  }, []);

  const generateProposal = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await aiApi.portfolioManager({
        positions: positions || [],
        cash: account ? Number(account.cash) : 0,
        portfolioValue: account ? Number(account.portfolio_value) : 0,
        riskTolerance,
      });
      setProposal(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="module-container">
      <div className="module-header">
        <h2>AI Stock Management <span className="status-pill paper">BETA</span></h2>
        <p>
          AI proposes specific portfolio actions based on your real holdings. Nothing executes automatically —
          you review and choose what to place, if anything.
        </p>
      </div>

      <div className="input-section">
        <div className="input-group">
          <label>Risk tolerance</label>
          <div className="order-type-selector">
            {RISK_LEVELS.map((r) => (
              <button key={r} className={riskTolerance === r ? 'active' : ''} onClick={() => setRiskTolerance(r)}>
                {r}
              </button>
            ))}
          </div>
        </div>
        <button className="primary-button" onClick={generateProposal} disabled={loading || !positions}>
          {loading ? 'Analyzing your portfolio…' : '✦ Generate AI proposal'}
        </button>
        <p className="input-hint">
          AI analysis is informational and not financial advice. Beta feature — review suggestions carefully.
        </p>
      </div>

      {error && <div className="error-box">{error}</div>}

      {proposal && (
        <div className="analysis-result">
          <div className="pretrade-card" style={{ marginBottom: 18 }}>
            <span className="ai-badge">✦ AI Manager Summary</span>
            <p className="reasoning">{proposal.summary}</p>
          </div>

          <div className="analysis-sections">
            {proposal.actions?.map((a, idx) => (
              <section key={idx} className="analysis-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                  <h3 style={{ margin: 0 }}>
                    {a.side === 'buy' ? '🔼' : '🔽'} {a.side.toUpperCase()} {a.qty} <StockLink symbol={a.symbol} />
                  </h3>
                  <span className={`recommendation-badge ${a.priority === 'HIGH' ? 'reconsider' : a.priority === 'MEDIUM' ? 'proceed_with_caution' : 'proceed'}`}>
                    {a.priority} priority
                  </span>
                </div>
                <p className="reasoning" style={{ marginBottom: 12 }}>
                  {a.rationale}
                </p>
                <button
                  className="secondary-button"
                  onClick={() => {
                    const symbol = String(a.symbol || '').toUpperCase().trim();
                    const side = a.side === 'sell' ? 'sell' : 'buy';
                    const qty = Math.max(1, Math.round(Number(a.qty) || 1));
                    if (onTradeAction && symbol) onTradeAction({ symbol, side, qty });
                  }}
                >
                  Open in Trade ticket →
                </button>
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
